/* eslint-disable import/no-anonymous-default-export */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';

import { ApiConfigProps } from '../infra/interfaces';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Não mostra toast para 400/402: quem chamou trata o erro sozinho. */
    silenciarAvisos?: boolean;
    /** Segunda tentativa depois de um 401: vai com o token novo e não renova de novo. */
    repetidaAposRenovar?: boolean;
  }
}

/** Disparado no `window` quando o 401 levou a sessão ao fim; o RenovadorDeSessao encerra a sessão. */
export const EVENTO_SESSAO_ENCERRADA = 'sessao:encerrada';

const ROTAS_DA_SESSAO = ['/api/autenticacao/refresh', '/api/autenticacao/logout'];

function ehRotaDaSessao(config?: AxiosRequestConfig): boolean {
  return ROTAS_DA_SESSAO.some((rota) => config?.url?.includes(rota));
}

export type DesfechoDaRenovacao = 'renovada' | 'sessao-encerrada' | 'falha-temporaria';

/**
 * 401 com refresh token: renova a sessão. Se o token enviado já não é o do
 * cookie (a tela guardou um velho ou outra aba renovou), já está renovada e
 * basta repetir com o do cookie. Quem chamou decide como repetir: o interceptor
 * do axios e o fetch do streaming passam por aqui. `session` importa este
 * arquivo, por isso o import dinâmico.
 */
export async function renovarAposNaoAutorizado(tokenEnviado: string): Promise<DesfechoDaRenovacao> {
  const tokenDoCookie = Cookies.get('token');
  const { renovarSessao, performLogout, SessaoRecusadaError } = await import('./session');

  try {
    if (!tokenDoCookie || tokenEnviado === tokenDoCookie) await renovarSessao();
  } catch (erroDaRenovacao) {
    if (!(erroDaRenovacao instanceof SessaoRecusadaError)) return 'falha-temporaria';

    // O RenovadorDeSessao da tela encerra a sessão e recarrega a página; sem ele, sai daqui.
    const tratado = !window.dispatchEvent(new Event(EVENTO_SESSAO_ENCERRADA, { cancelable: true }));

    if (!tratado) {
      await performLogout();
      window.location.assign('/login');
    }

    return 'sessao-encerrada';
  }

  return 'renovada';
}

async function renovarERepetir(api: AxiosInstance, error: any): Promise<AxiosResponse> {
  const config: AxiosRequestConfig = error.response.config;
  const tokenEnviado = String(config.headers?.Authorization ?? '').replace(/^bearer /i, '');
  const desfecho = await renovarAposNaoAutorizado(tokenEnviado);

  if (desfecho === 'falha-temporaria') {
    toast.warn('Acesso não autorizado');
    return Promise.reject(error);
  }

  // A página vai recarregar: rejeitar só faria a tela mostrar erro por cima do login.
  if (desfecho === 'sessao-encerrada') return new Promise<never>(() => undefined);

  return api.request({ ...config, repetidaAposRenovar: true });
}

/** Endereço do servidor On-Premises gravado no login. Todos os serviços usam este. */
export function configuracaoPadrao(): ApiConfigProps {
  return { url: localStorage.getItem('url') ?? '' };
}

export function enderecoBase(apiConfig: ApiConfigProps = configuracaoPadrao()): string | undefined {
  return apiConfig.url ? apiConfig.url : process.env.REACT_APP_API_HUBBY;
}

/**
 * Os dois cabeçalhos que toda chamada ao servidor leva. Fonte única para o
 * interceptor do axios e para quem chama por fetch (o streaming da análise).
 */
export function cabecalhosDaSessao(
  apiConfig: ApiConfigProps = configuracaoPadrao(),
  repetidaAposRenovar = false
): { Authorization: string; 'id-sessao': string } {
  const token = apiConfig.token && !repetidaAposRenovar ? apiConfig.token : Cookies.get('token');

  return {
    Authorization: `bearer ${token}`,
    'id-sessao': apiConfig.idSession
      ? apiConfig.idSession
      : (localStorage.getItem('id-sessao') as string),
  };
}

export default (apiConfig: ApiConfigProps) => {
  const api = axios.create();

  api.interceptors.request.use((config: AxiosRequestConfig) => {
    config.baseURL = enderecoBase(apiConfig);

    config.headers = {
      ...config.headers,
      'Access-Control-Allow-Headers':
        'Accept, Accept-CH, Accept-Charset, Accept-Datetime, Accept-Encoding, Accept-Ext, Accept-Features, Accept-Language, Accept-Params, Accept-Ranges, Access-Control-Allow-Credentials, Access-Control-Allow-Headers, Access-Control-Allow-Methods, Access-Control-Allow-Origin, Access-Control-Expose-Headers, Access-Control-Max-Age, Access-Control-Request-Headers, Access-Control-Request-Method, Age, Allow, Alternates, Authentication-Info, Authorization, C-Ext, C-Man, C-Opt, C-PEP, C-PEP-Info, CONNECT, Cache-Control, Compliance, Connection, Content-Base, Content-Disposition, Content-Encoding, Content-ID, Content-Language, Content-Length, Content-Location, Content-MD5, Content-Range, Content-Script-Type, Content-Security-Policy, Content-Style-Type, Content-Transfer-Encoding, Content-Type, Content-Version, Cookie, Cost, DAV, DELETE, DNT, DPR, Date, Default-Style, Delta-Base, Depth, Derived-From, Destination, Differential-ID, Digest, ETag, Expect, Expires, Ext, From, GET, GetProfile, HEAD, HTTP-date, Host, IM, If, If-Match, If-Modified-Since, If-None-Match, If-Range, If-Unmodified-Since, Keep-Alive, Label, Last-Event-ID, Last-Modified, Link, Location, Lock-Token, MIME-Version, Man, Max-Forwards, Media-Range, Message-ID, Meter, Negotiate, Non-Compliance, OPTION, OPTIONS, OWS, Opt, Optional, Ordering-Type, Origin, Overwrite, P3P, PEP, PICS-Label, POST, PUT, Pep-Info, Permanent, Position, Pragma, ProfileObject, Protocol, Protocol-Query, Protocol-Request, Proxy-Authenticate, Proxy-Authentication-Info, Proxy-Authorization, Proxy-Features, Proxy-Instruction, Public, RWS, Range, Referer, Refresh, Resolution-Hint, Resolver-Location, Retry-After, Safe, Sec-Websocket-Extensions, Sec-Websocket-Key, Sec-Websocket-Origin, Sec-Websocket-Protocol, Sec-Websocket-Version, Security-Scheme, Server, Set-Cookie, Set-Cookie2, SetProfile, SoapAction, Status, Status-URI, Strict-Transport-Security, SubOK, Subst, Surrogate-Capability, Surrogate-Control, TCN, TE, TRACE, Timeout, Title, Trailer, Transfer-Encoding, UA-Color, UA-Media, UA-Pixels, UA-Resolution, UA-Windowpixels, URI, Upgrade, User-Agent, Variant-Vary, Vary, Version, Via, Viewport-Width, WWW-Authenticate, Want-Digest, Warning, Width, X-Content-Duration, X-Content-Security-Policy, X-Content-Type-Options, X-CustomHeader, X-DNSPrefetch-Control, X-Forwarded-For, X-Forwarded-Port, X-Forwarded-Proto, X-Frame-Options, X-Modified, X-OTHER, X-PING, X-PINGOTHER, X-Powered-By, X-Requested-With',
      ...cabecalhosDaSessao(apiConfig, config.repetidaAposRenovar),
    };

    return config;
  });

  api.interceptors.response.use(
    (response: AxiosResponse<any>) => response,
    (error: any) => {
      // Falha de rede e timeout chegam sem `response`. Sem esta saída o próprio
      // interceptor estourava um TypeError, escondendo o erro de verdade.
      if (!error?.response) return Promise.reject(error);

      const verifyUrl = error.response.config?.url?.includes('ListarDashboardsLoja');
      if (
        verifyUrl &&
        error.response.data?.notifications?.[0]?.includes(
          'Não foram encontrados dashboards para download.'
        )
      )
        error.response.data.notifications[0] =
          'Não foi possível encontrar um dashboard com os filtros informados!';
      switch (error.response.status) {
        case 400:
        case 402:
          // Regra de negócio recusada: o corpo traz o motivo escrito para quem
          // usa o painel, então a promise resolve e quem chamou lê `notifications`.
          // Chamada de bastidor (o menu lateral, por exemplo) não avisa em toast:
          // "Nenhum dashboard favoritado" viraria um aviso em toda troca de tela.
          if (!error.response.config?.silenciarAvisos) {
            error.response.data?.notifications?.forEach((notification: string) => {
              toast.warn(notification);
            });
          }

          return error.response.data;
        case 401:
          // Refresh e logout correm por trás da tela: o 401 deles não vira aviso.
          if (ehRotaDaSessao(error.response.config)) break;

          if (Cookies.get('refreshToken') && !error.response.config?.repetidaAposRenovar)
            return renovarERepetir(api, error);

          // Sem contexto de tela para dar: o aviso genérico é o melhor que dá.
          toast.warn('Acesso não autorizado');
          break;
        default:
          // 409, 5xx e o resto: a promise **rejeita**. Quem chamou sabe o que
          // estava fazendo e escreve a mensagem certa — ou pinta o estado de
          // erro da tela. Resolver com `undefined` aqui fazia um 503 chegar às
          // listas como "nenhum item", que é o pior desfecho possível.
          break;
      }

      return Promise.reject(error);
    }
  );

  return api;
};
